import { featureOn } from "@lib/featureFlipper";
import fetch from "@lib/fetch";

import { isDustUnreachableInTab, subscribeToDustUnreachable } from "./dustUnreachableLatch";
import { whenDustSingletonExists } from "./getDustSingleton";
import { getRealtimeTransport } from "./getRealtimeTransport";

const claimRealtimeTransportUrl = "/pusher/claim_realtime_transport";

/**
 * Claims an async request's result on the transport this tab can receive once that changes
 * while the request is pending. The backend answers on every transport a request for the job
 * claimed, and re-pushes a result that already went out, so the claim never runs the job again.
 *
 * - A request sent while DUST was not connected claimed Pusher, and Pusher may be the transport
 *   failing this tab (it is disabled outright in CI e2e). The first time DUST connects, this
 *   claims DUST.
 * - A request that claimed DUST is stranded if the tab gives up on DUST. When the unreachable
 *   latch trips, this claims Pusher. A plain reconnect needs nothing: DUST replays a
 *   developer's retained messages.
 *
 * It claims at most once: after that, the job answers on both transports.
 *
 * Call right before sending the original request, since it reads the transport that request
 * will claim.
 *
 * @param jobToken Job token of the pending request.
 * @param onClaimFailed Called when the claim request fails. The claim is best-effort, since the
 *   result can still arrive on the original transport, so a failure should be logged, not fail
 *   the request.
 * @returns `dispose`, to call once the result has arrived or the request is abandoned.
 */
export const claimTransportWhenDustChanges = (
  jobToken: string,
  onClaimFailed: (error: unknown) => void
): { dispose: () => void } => {
  if (!featureOn("dust_enable_dashboard_connections") || !featureOn("dust_realtime_subscriptions")) {
    return { dispose: () => {} };
  }

  let disposed = false;
  const stopWatching: Array<() => void> = [];
  const dispose = () => {
    disposed = true;
    stopWatching.forEach((stop) => stop());
    stopWatching.length = 0;
  };
  const claim = () => {
    if (disposed) {
      return;
    }
    dispose();
    fetch(claimRealtimeTransportUrl, "POST", { job_token: jobToken }).catch(onClaimFailed);
  };

  if (getRealtimeTransport() === "dust") {
    stopWatching.push(
      subscribeToDustUnreachable(() => {
        if (isDustUnreachableInTab()) {
          claim();
        }
      })
    );
  } else {
    stopWatching.push(
      whenDustSingletonExists((dustProvider) => {
        if (disposed) {
          return;
        }
        stopWatching.push(
          dustProvider.onConnectionStateChange((connected) => {
            if (connected) {
              claim();
            }
          })
        );
      })
    );
  }

  return { dispose };
};
